import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import useTableColumnVisibility from '@/shared/components/dataDisplay/DataGridTable/hooks/useTableColumnVisibility';

import Package from '../types/package.type';

const usePackagingDetailsTableForm_v2 = () => {
  const { columns, visibilityModel, setVisibilityModel } =
    useTableColumnVisibility({
      tableColumnsList: [
        'package_number',
        'package_assigned_items',
        'package_weight',
        'package_dimensions',
        'package_description',
      ],
      tableRequiredColumns: [
        'package_number',
        'package_assigned_items',
        'package_weight',
        'package_dimensions',
        'package_description',
        'rowActions',
      ],
    });

  const defaultPackage: Package = {
    package_id: uuidv4(),
    package_number: 1,
    package_assigned_items: 0,
    package_description: 'Pet supplies',
    package_weight: {
      unit: 'lb',
      value: 2,
    },
    package_cuboid: {
      h: 10,
      w: 10,
      l: 10,
      unit: 'in',
    },
  };

  const [packages, setPackages] = useState<Package[]>([defaultPackage]);

  const handleAddPackage = () => {
    setPackages((prev) => [
      ...prev,
      {
        ...defaultPackage,
        package_number: prev.length + 1,
      },
    ]);
  };

  const handleRemovePackage = (id: string) => {
    setPackages((prev) =>
      prev.filter((packageItem) => packageItem.package_id !== id),
    );
  };

  const handleAssignItemsToPackage = (value: {
    packageId: string;
    assignedItems: number;
  }) => {
    setPackages((prev) =>
      prev.map((item) =>
        item.package_id === value.packageId
          ? { ...item, package_assigned_items: value.assignedItems }
          : { ...item, package_assigned_items: 0 },
      ),
    );
  };

  const handleUpdateWeight = (
    value: Pick<Package, 'package_id' | 'package_weight'>,
  ) => {
    setPackages((prev) =>
      prev.map((item) =>
        item.package_id === value.package_id
          ? {
              ...item,
              package_weight: {
                ...item.package_weight,
                ...value.package_weight,
              },
            }
          : item,
      ),
    );
  };

  const handleUpdateCuboid = (
    value: Pick<Package, 'package_id' | 'package_cuboid'>,
  ) => {
    setPackages((prev) =>
      prev.map((item) =>
        item.package_id === value.package_id
          ? {
              ...item,
              package_cuboid: {
                ...item.package_cuboid,
                ...value.package_cuboid,
              },
            }
          : item,
      ),
    );
  };

  const handleUpdateDescription = (
    value: Pick<Package, 'package_id' | 'package_description'>,
  ) => {
    setPackages((prev) =>
      prev.map((item) =>
        item.package_id === value.package_id
          ? {
              ...item,
              package_description: value.package_description,
            }
          : item,
      ),
    );
  };

  return {
    columns,
    packages,
    visibilityModel,
    setVisibilityModel,
    handleAddPackage,
    handleRemovePackage,
    handleAssignItemsToPackage,
    handleUpdateWeight,
    handleUpdateCuboid,
    handleUpdateDescription,
  };
};

export default usePackagingDetailsTableForm_v2;
